import type { ReactNode } from 'react';

/**
 * The block editor's own shapes (E14-design §5, ADR-025 decisions 6 and 7).
 * The editor is report-agnostic: a report maps its blocks, its token table
 * and its three calls (rewrite, save, revert) onto these, so the Health &
 * Readiness report can adopt the same editor later.
 */

/**
 * `paragraph` and `bullets` (E14); `headline` (one sentence), `vendor_card`
 * (a rank label, a body and a Watch line, with two figures by token) and
 * `scenarios` (numbered demonstration steps with RTM numbers) since E24
 * (ADR-026 decision 6). The text form of the last two is in `block-forms.ts`.
 */
export type BlockKind = 'paragraph' | 'bullets' | 'headline' | 'vendor_card' | 'scenarios';
export type BlockOrigin = 'ai_drafted' | 'template' | 'consultant_edited' | 'ai_rewritten';

export interface EditorCitation {
  id: string;
  /** The chip's label: the record id the report prints ("VK-00905", "RTM 39") or a source's title. */
  label: string;
  /** An AI confidence, when the citation has one; otherwise the chip shows none. */
  confidence?: number;
  /** What the citation points at (`engagement_requirement` feeds a scenarios block's RTM column). */
  kind?: string;
  /** For a list block: the 1-based lines this citation supports; absent: the whole block. */
  lines?: number[];
}

/** A heading a run of blocks sits under, such as the vendor a shortlist group is about. */
export interface BlockGroup {
  id: string;
  title: string;
  /** A line under the heading. */
  note?: string | null;
}

export interface EditorBlock {
  id: string;
  kind: BlockKind;
  /** The current text, with `{{token}}` figures. Bullets: one item per line. */
  text: string;
  origin: BlockOrigin;
  citations: EditorCitation[];
  /** The instruction of the last accepted rewrite. */
  lastInstruction?: string | null;
  editedBy?: string | null;
  editedAt?: string | null;
  /** Why the block needs a person: the AI could not draft it with citations. */
  flag?: string | null;
  group?: BlockGroup | null;
  /**
   * A `vendor_card` block's code-composed parts: the vendor's name, its
   * rank (printed before the rank label) and the figures printed under the
   * card, each a token the report fills.
   */
  card?: VendorCardData | null;
}

export interface VendorCardData {
  name: string;
  /** The product's rank in the fit; null while unranked. */
  rank: number | null;
  figures: Array<{ label: string; token: string }>;
}

export interface EditorSection {
  key: string;
  title: string;
  description?: string | null;
  /** Keep the title for readers only: the page draws its own heading (E24's pages). */
  titleHidden?: boolean;
  /** Classes on the section, for a page that lays its blocks out in a grid (vendor cards abreast). */
  className?: string;
  blocks: EditorBlock[];
  /** What code composed for the section (a table), shown before or after its blocks. */
  before?: ReactNode;
  after?: ReactNode;
}

export const REWRITE_PRESETS = ['shorter', 'more_formal', 'plainer', 'add_a_point'] as const;
export type RewritePreset = (typeof REWRITE_PRESETS)[number];

export const PRESET_LABEL: Record<RewritePreset, string> = {
  shorter: 'Shorter',
  more_formal: 'More formal',
  plainer: 'Plainer',
  add_a_point: 'Add a point',
};

export type RewriteRequest = { preset: RewritePreset } | { instruction: string };

/** A guard's finding on a proposal (T-0270): `unknown_token`, `new_figure`, `foreign_citation`, `lost_all_citations`. */
export interface RewriteIssue {
  kind: string;
  /** What it found, when it names something: the token, the figure. */
  text?: string | null;
  /** The server's sentence, shown for a kind the editor has no words for. */
  message?: string | null;
}

export interface RewriteProposal {
  text: string;
  citations: EditorCitation[];
}

export interface RewriteResult {
  proposal: RewriteProposal | null;
  issues: RewriteIssue[];
  /** The instruction as the server will record it on Accept; else the editor's own words. */
  instruction?: string;
}

export interface SaveBlockInput {
  text: string;
  origin: 'consultant_edited' | 'ai_rewritten';
  /** The rewrite's instruction or preset, for the audit. */
  instruction?: string;
  /** The accepted rewrite's citation ids. Absent on a hand edit, which keeps the block's own. */
  citations?: string[];
}

export interface ReportEditorCalls {
  onRewrite: (blockId: string, request: RewriteRequest) => Promise<RewriteResult>;
  onSave: (blockId: string, input: SaveBlockInput) => Promise<unknown>;
  onRevert: (blockId: string) => Promise<unknown>;
}

/** The words a request is audited under: a preset's label, or the instruction typed. */
export function instructionOf(request: RewriteRequest): string {
  return 'preset' in request ? PRESET_LABEL[request.preset] : request.instruction;
}
